import type { UseQueryOptions, UseQueryResult } from 'react-query';
import { useQuery } from 'react-query';

import type { IMakeOptional, IQueryKeyFactory } from '~/helpers/types';
import type { ApiError } from '~/lib/api';
import type { IApiGetOrganizationUserResponseBody } from '../OrganizationsApi.types';
import { useApi } from '~/lib/api/useApi';

export type IUseOrganizationUserQueryValues = {
  organizationId: string;
  userId: string;
};

export const organizationUserQueryKeyFactory: IQueryKeyFactory<
  IUseOrganizationUserQueryValues
> = (values) =>
  values &&
  (['organizationUsers', values.organizationId, values.userId] as const);

export const useOrganizationUserQuery = (
  valuesProp?: IMakeOptional<IUseOrganizationUserQueryValues, 'userId'>,
  options?: UseQueryOptions<IApiGetOrganizationUserResponseBody, ApiError>,
): UseQueryResult<IApiGetOrganizationUserResponseBody, ApiError> => {
  const api = useApi();
  const userId = valuesProp
    ? (valuesProp.userId ?? api.getCurrentUserId())
    : undefined;
  const values: IUseOrganizationUserQueryValues | undefined =
    valuesProp && userId
      ? {
          organizationId: valuesProp.organizationId,
          userId,
        }
      : undefined;

  return useQuery({
    queryKey: organizationUserQueryKeyFactory(values),
    queryFn: values
      ? () => api.organizations.getUser(values.organizationId, values.userId)
      : undefined,
    enabled: !!values,
    ...options,
  });
};
